/**
 * Downloading the Extra Locale 教師參考手冊 (teacher reference manual) PDF.
 *
 * The problem this solves is the FILENAME. Manuals live in the public blob
 * container under a generated name — `2026-9-10/1757481600___a1b2c3d4e5f60718.pdf`
 * — so a plain <a href> to the blob hands the teacher that string to save, for
 * a file the whole feature calls 「GS08 [TC] (26/27) 山頂 教師參考手冊」. A
 * cross-origin <a download> does not help: the `download` attribute's filename
 * is ignored unless the href is same-origin (or a blob:/data: URL), which is
 * precisely the case it would be needed for.
 *
 * So: fetch the bytes, wrap them in an object URL — same-origin by construction,
 * so `download` is honoured — and click that. `files.ev-cuhk.net` does send
 * `access-control-allow-origin: https://ev-cuhk.net` (verified 2026-09-10), and
 * on localhost the dev server's /evfiles proxy makes the fetch same-origin
 * anyway, so the fetch path is the normal one in both environments.
 *
 * IT STILL FALLS BACK, because the fetch is the half that can fail for reasons
 * that have nothing to do with the file: a CORS rule edited on the storage
 * account, Safari's stricter enforcement, an offline blip. Opening the blob URL
 * in a new tab always works (the container is anonymous-read) and shows the PDF
 * in the browser's viewer — a worse filename, but the manual in front of the
 * teacher, which is the point. Failing loudly instead would trade a cosmetic
 * problem for a broken button.
 */
import Config from '../Config.jsx';

/** Absolute URL of a `file` row's blob, or "" when there is no path. */
export function manualUrl(path) {
  const clean = String(path || '').trim();
  if (clean === '') {
    return '';
  }
  return Config.UPLOAD_PATH() + clean;
}

// Windows rejects \ / : * ? " < > | in a filename, and every OS dislikes control
// characters. A manual's name is teacher-authored Chinese carrying parentheses
// and a slash (「(26/27)」), so this is not a theoretical case. Everything else —
// CJK, spaces, brackets — is left alone.
const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|]/g;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;

/**
 * What to save the manual as: "<display name> <suffix>.pdf", cleaned.
 * Falls back to the blob's own basename when the row carries no display name.
 */
export function manualFileName(displayName, path, suffix = '') {
  let base = String(displayName || '').trim();
  if (base === '') {
    const parts = String(path || '').split('/');
    base = (parts[parts.length - 1] || 'manual').replace(/\.[^.]*$/, '');
  }
  if (suffix && String(suffix).trim() !== '') {
    base = base + ' ' + String(suffix).trim();
  }
  base = base
    .replace(CONTROL_CHARS, '')
    .replace(ILLEGAL_FILENAME_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (base === '') {
    base = 'manual';
  }
  // 255 is the practical per-component limit on ext4/APFS/NTFS; leave room for
  // the extension and for the "(1)" a browser appends to a duplicate.
  if (base.length > 200) {
    base = base.slice(0, 200).trim();
  }
  return base.toLowerCase().endsWith('.pdf') ? base : base + '.pdf';
}

function openInNewTab(url) {
  // noopener: the blob host is not ours to trust with window.opener.
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (win) {
    win.opener = null;
  }
  return !!win;
}

function saveBlob(blob, fileName) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  link.rel = 'noopener';
  // Must be in the document for Firefox to honour the click.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Deferred: revoking synchronously races the download in Safari, which has
  // not yet read the blob when click() returns.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60 * 1000);
}

/**
 * Download one manual. Resolves to how it was delivered — 'download' (the
 * proper filename), 'newtab' (the fallback), or 'none' (nothing to download,
 * or a popup blocker ate the fallback) — so a caller can notify accordingly.
 *
 * Never rejects: every failure that matters here is one the fallback covers.
 */
export async function downloadManual({ path, displayName, suffix = '' } = {}) {
  const url = manualUrl(path);
  if (url === '') {
    return 'none';
  }
  const fileName = manualFileName(displayName, path, suffix);

  try {
    const response = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' });
    if (!response.ok) {
      throw new Error('HTTP ' + response.status);
    }
    const blob = await response.blob();
    if (blob.size === 0) {
      throw new Error('empty response');
    }
    saveBlob(blob, fileName);
    return 'download';
  } catch (error) {
    console.log('manual download fell back to a new tab:', error);
    return openInNewTab(url) ? 'newtab' : 'none';
  }
}

export default downloadManual;
